import { defaultFormatChars } from '../constants';
import { MaskItem, MaskOptions, FormatChars } from '../types';

interface ParseMaskOptions {
  mask?: string | (string | RegExp)[];
  maskPlaceholder?: string | null;
  formatChars?: FormatChars;
}

const toMaskItems = (
  mask: string | (string | RegExp)[],
  currentFormatChars: FormatChars,
): MaskItem[] => {
  if (typeof mask !== 'string') {
    return mask.map((item) =>
      typeof item === 'string'
        ? { editable: false, char: item }
        : { editable: true, rule: item },
    );
  }

  const items: MaskItem[] = [];
  let isEscaped = false;

  mask.split('').forEach((character) => {
    if (!isEscaped && character === '\\') {
      isEscaped = true;
      return;
    }

    const rule = isEscaped ? undefined : currentFormatChars[character];

    items.push(
      rule ? { editable: true, rule } : { editable: false, char: character },
    );
    isEscaped = false;
  });

  return items;
};

export default function parseMask({
  mask,
  maskPlaceholder,
  formatChars,
}: ParseMaskOptions): MaskOptions | null {
  if (!mask) {
    return null;
  }

  const parsedMask = toMaskItems(mask, formatChars ?? defaultFormatChars);
  const literals = parsedMask.map((item) => (item.editable ? '' : item.char));

  let parsedMaskPlaceholder: string | null = maskPlaceholder || null;

  if (parsedMaskPlaceholder) {
    if (parsedMaskPlaceholder.length === 1) {
      const placeholder = parsedMaskPlaceholder;

      parsedMaskPlaceholder = parsedMask
        .map((item, position) =>
          item.editable ? placeholder : literals[position],
        )
        .join('');
    } else {
      const placeholderArray = parsedMaskPlaceholder.split('');

      parsedMask.forEach((item, position) => {
        if (!item.editable) {
          placeholderArray[position] = literals[position];
        }
      });

      parsedMaskPlaceholder = placeholderArray.join('');
    }
  }

  let prefix = '';
  let prefixPosition = 0;

  while (
    prefixPosition < parsedMask.length &&
    !parsedMask[prefixPosition].editable
  ) {
    prefix += literals[prefixPosition];
    prefixPosition++;
  }

  let lastEditablePosition = parsedMask.length - 1;

  while (
    lastEditablePosition >= 0 &&
    !parsedMask[lastEditablePosition].editable
  ) {
    lastEditablePosition--;
  }

  return {
    maskPlaceholder: parsedMaskPlaceholder,
    prefix,
    mask: parsedMask,
    literals,
    lastEditablePosition,
  };
}
